<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-1.5">
                <WalletMinimal class="size-4 text-primary" />
                <CardTitle class="text-sm font-medium text-primary">
                    Plugin subscription
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent class="flex flex-col gap-6">
            <!-- Trial-ended warning -->
            <div
                v-if="!pluginSubscription && !shouldAllowTrial && trialHasEnded"
                class="flex items-start gap-3 bg-new-amber-100 px-4 py-3"
            >
                <TriangleAlert class="mt-0.5 size-5 shrink-0 text-new-amber-600" />
                <div class="flex flex-col gap-1 text-new-amber-700">
                    <p class="text-sm font-semibold leading-5 text-new-amber-800">
                        Your free 14-days trial period has ended<span v-if="trialEndedAt"> on {{ trialEndedAt }}</span>.
                    </p>
                    <p class="text-xs leading-5">
                        Your Plugin API Tokens are now suspended, please select appropriate Plugin subscription to continue.
                    </p>
                </div>
            </div>

            <!-- Trial-still-active info -->
            <div
                v-if="!pluginSubscription && !shouldAllowTrial && !trialHasEnded && trialEndedAt"
                class="flex items-start gap-3 border border-new-neutral-200 bg-new-neutral-50 px-4 py-3"
            >
                <Info class="mt-0.5 size-5 shrink-0 text-new-neutral-700" />
                <div class="flex flex-col gap-1 text-new-neutral-700">
                    <p class="text-sm font-medium leading-5 text-foreground">
                        Your free 14-days trial period will end on {{ trialEndedAt }}.
                    </p>
                    <p class="text-xs leading-5 text-muted-foreground">
                        Pick a subscription plan before then to keep your Plugin API Tokens active.
                    </p>
                </div>
            </div>

            <!-- Plan selection header -->
            <div class="flex flex-col gap-2">
                <h3 class="text-lg leading-none text-foreground">
                    Choose your Plugin subscription plan
                </h3>
                <p class="text-sm text-muted-foreground">
                    Plans differ in included users, additional-user pricing, and available features. See the
                    <a
                        href="https://topol.io/tariff-plugin#plugin-pricing"
                        target="_blank"
                        class="font-medium underline text-new-blue-500 hover:text-new-blue-600"
                    >full comparison</a>
                    to find the plan that fits your needs.
                </p>
            </div>

            <!-- Plan cards (stacked) -->
            <div class="flex flex-col gap-3">
                <div
                    v-for="plan in sortedPlans"
                    :key="plan.uuid"
                    class="flex p-4"
                    :class="isActivePlan(plan.uuid)
                        ? 'bg-background border-[3px] border-new-neutral-200 dark:border-border'
                        : 'bg-new-neutral-100 border border-new-neutral-200 dark:bg-muted dark:border-border'"
                >
                    <div class="flex w-full flex-col gap-4">
                        <div class="flex flex-col gap-1">
                            <div class="flex items-center gap-2 text-sm leading-5">
                                <span class="font-medium text-foreground">{{ plan.name }}</span>
                                <span class="font-light text-foreground">
                                    (${{ plan.price_for_month / 100 }}/month)
                                </span>
                            </div>
                            <p class="text-xs leading-4 text-muted-foreground">
                                {{ plan.description }}
                                <span v-if="plan.users_limit">
                                    {{ plan.users_limit }} users included in plan, additional user for ${{ (plan.users_price || 0) / 100 }}/month.
                                </span>
                                <span v-else>Unlimited users.</span>
                                Paid {{ plan.period === 12 ? 'annually' : 'monthly' }}.
                            </p>
                        </div>

                        <p
                            v-if="isActivePlan(plan.uuid) && pluginSubscription?.canceled_at"
                            class="text-xs leading-4 text-new-amber-700"
                        >
                            Subscription canceled — access remains until
                            <span class="font-medium">{{ format(new Date(pluginSubscription.canceled_at), 'd MMMM, yyyy') }}</span>.
                            <span v-if="plan.archived_at && isStripeEnabled">This plan is no longer offered, pick another plan to continue.</span>
                        </p>

                        <p
                            v-else-if="isActivePlan(plan.uuid) && pluginSubscription?.next_billing_date"
                            class="text-xs leading-4 text-muted-foreground"
                        >
                            Renews on
                            <span class="font-medium text-foreground">{{ format(new Date(pluginSubscription.next_billing_date), 'd MMMM, yyyy') }}</span>.
                        </p>

                        <div class="flex items-center gap-2">
                            <template v-if="isActivePlan(plan.uuid)">
                                <span
                                    class="text-sm font-semibold leading-5"
                                    :class="pluginSubscription?.canceled_at ? 'text-new-amber-600' : 'text-new-green-600'"
                                >
                                    {{ pluginSubscription?.canceled_at ? 'Canceling' : 'Active' }}
                                </span>
                                <Button
                                    v-if="!pluginSubscription?.canceled_at"
                                    variant="outline"
                                    size="sm"
                                    :disabled="pluginSubscription?.recurring_payment_request"
                                    @click="$emit('openCancelModal')"
                                >
                                    Cancel plan
                                </Button>
                                <Button
                                    v-else-if="isStripeEnabled && !plan.archived_at"
                                    size="sm"
                                    :disabled="pluginSubscription?.recurring_payment_request"
                                    @click="$emit('openOrderModal', plan.uuid)"
                                >
                                    Subscribe again
                                </Button>
                                <Button
                                    v-else-if="!isStripeEnabled"
                                    size="sm"
                                    :disabled="pluginSubscription?.recurring_payment_request"
                                    @click="$emit('openSwitchModal')"
                                >
                                    Resume plan
                                </Button>
                            </template>
                            <Button
                                v-else-if="pluginSubscription && pluginSubscription.canceled_at && isStripeEnabled"
                                size="sm"
                                :disabled="pluginSubscription?.recurring_payment_request"
                                @click="$emit('openOrderModal', plan.uuid)"
                            >
                                Subscribe to this plan
                            </Button>
                            <Button
                                v-else-if="pluginSubscription"
                                size="sm"
                                :disabled="pluginSubscription?.recurring_payment_request"
                                @click="$emit('openSwitchModal', plan.uuid)"
                            >
                                Switch plan
                            </Button>
                            <Button
                                v-else
                                size="sm"
                                @click="$emit('openOrderModal', plan.uuid)"
                            >
                                Subscribe now
                            </Button>
                        </div>
                    </div>
                </div>

                <!-- Compare plans link -->
                <a
                    href="https://topol.io/tariff-plugin#plugin-pricing"
                    target="_blank"
                    class="text-xs font-semibold underline text-new-blue-500 hover:text-new-blue-600"
                >
                    Compare all plans
                </a>
            </div>

            <!-- Free Trial -->
            <div v-if="shouldAllowTrial && !pluginSubscription" class="border-t border-new-neutral-200 pt-6">
                <Button :disabled="trialLoading" @click="startTrial">
                    Start 14-days FREE Trial
                </Button>
            </div>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { ref, computed, PropType } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { format } from 'date-fns';
import { toast } from 'vue-sonner';
import { WalletMinimal, TriangleAlert, Info } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SubscriptionPlan from '@/types/Subscription/SubscriptionPlan';
import ISubscription from '@/types/Subscription/ISubscription';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import useFeatures from '@/composables/useFeatures';
import { useGtm } from '@/composables/useGtm';

const props = defineProps({
    activeSubscriptionPlans: {
        type: Array as PropType<SubscriptionPlan[]>,
        required: true,
    },
    pluginSubscription: {
        type: Object as PropType<ISubscription | null>,
        default: null,
    },
    shouldAllowTrial: {
        type: Boolean,
        default: false,
    },
});

defineEmits<{
    openOrderModal: [planUuid: string];
    openSwitchModal: [];
    openCancelModal: [];
}>();

const trialLoading = ref(false);

const page = usePage<ITopolPageProps>();
const { teamFeatureDisabled } = useFeatures();
const isStripeEnabled = computed(() => !teamFeatureDisabled('stripe'));

const trialEndsAtDate = computed(() => {
    const raw = page.props.user?.plugin_trial_ends_at;
    return raw ? new Date(raw) : null;
});

const trialEndedAt = computed(() => {
    return trialEndsAtDate.value ? format(trialEndsAtDate.value, 'MMMM d, yyyy') : null;
});

// Only treat the trial as ended if the end date has actually passed.
// A future date means the trial is still active — don't show the
// "your trial has ended" banner in that case.
const trialHasEnded = computed(() => {
    return !!trialEndsAtDate.value && trialEndsAtDate.value.getTime() <= Date.now();
});

const sortedPlans = computed(() => {
    return [...props.activeSubscriptionPlans].sort((a, b) => a.price - b.price);
});

const isActivePlan = (uuid: string) => {
    return props.pluginSubscription?.subscription_plan_uuid === uuid;
};

const { pushEvent } = useGtm();

const startTrial = () => {
    trialLoading.value = true;

    router.post(
        route('plugin.enable'),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                pushEvent('topol_plugin_trial_start');

                toast.success('Free trial started! You have 14 days to explore.');
            },
            onError: () => {
                toast.error('Failed to start trial');
            },
            onFinish: () => {
                trialLoading.value = false;
            },
        }
    );
};
</script>
